/**
 * Payment Callback Page - Return page after Tap payment gateway
 * Verifies payment status and shows success/failure message
 * Redirects to appropriate page based on payment result
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaHome, FaCalendar } from 'react-icons/fa';
import { getPaymentStatus, getBookingDetails } from './paymentService';
import { getApiBaseUrl } from './config/apiConfig';

const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      setLoading(true);

      // Get tap_id from URL parameters
      const tapId = searchParams.get('tap_id');
      const bookingId = searchParams.get('bookingId') || searchParams.get('booking_id'); // Support both formats
      const isMockPayment = searchParams.get('mock') === 'true';

      console.log('🔍 Payment Callback Page - URL Parameters:', {
        tapId,
        bookingId,
        isMockPayment,
        allParams: Object.fromEntries(searchParams.entries())
      });

      if (!tapId) {
        throw new Error('Payment ID not found');
      }

      // Verify payment status with backend
      const result = await getPaymentStatus(tapId);

      if (!result.success) {
        throw new Error(result.error || 'Failed to verify payment');
      }

      const status = result.charge?.status;
      setPaymentStatus(status);

      // Check if payment was successful
      if (status === 'CAPTURED' || status === 'AUTHORIZED') {
        setPaymentSuccess(true);

        // Get booking details
        if (bookingId) {
          const bookingResult = await getBookingDetails(bookingId);
          if (bookingResult.success) {
            setBooking(bookingResult.booking);
          }
        }

        // 🧪 TEST MODE: Trigger mock payment confirmation to send emails
        if (isMockPayment && bookingId) {
          console.log('🧪 Mock payment detected - triggering confirmation emails...');
          console.log('🧪 Confirmation request data:', { chargeId: tapId, bookingId });
          try {
            const token = await import('./authService').then(m => m.getAccessToken());
            console.log('🧪 Auth token obtained:', token ? 'Yes' : 'No');

            const response = await fetch(`${getApiBaseUrl()}/api/payment/confirm-mock`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                chargeId: tapId,
                bookingId: bookingId
              })
            });

            console.log('🧪 Confirmation response status:', response.status);
            const confirmResult = await response.json();
            console.log('🧪 Confirmation result:', confirmResult);

            if (confirmResult.success) {
              console.log('✅ Mock payment confirmed, emails sent');
            } else {
              console.error('❌ Mock confirmation failed:', confirmResult.error);
            }
          } catch (confirmError) {
            console.error('❌ Error triggering mock confirmation:', confirmError);
            // Don't fail the whole payment flow if email fails
          }
        } else {
          console.log('🧪 Mock confirmation skipped:', { isMockPayment, hasBookingId: !!bookingId });
        }
      } else {
        setPaymentSuccess(false);
        setError(`Payment ${status.toLowerCase()}`);
      }

      setLoading(false);
    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.message || 'Failed to verify payment');
      setPaymentSuccess(false);
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard/student');
  };

  const handleViewBooking = () => {
    navigate('/dashboard/student?tab=bookings');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-blue-50">
        <div className="text-center bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full mx-4">
          <FaSpinner className="animate-spin text-6xl text-orange-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="text-center bg-white rounded-2xl shadow-2xl p-12 max-w-2xl w-full">
          <div className="mb-6">
            <div className="mx-auto w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <FaCheckCircle className="text-5xl text-green-500" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
            <p className="text-xl text-gray-600">Your session has been confirmed</p>
          </div>

          {booking && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-6 text-left border-2 border-green-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Session Details</h3>
              <div className="space-y-2 text-gray-700">
                <div className="flex justify-between">
                  <span className="font-semibold">Teacher:</span>
                  <span>{booking.teacher_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Date:</span>
                  <span>{booking.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Time:</span>
                  <span>{booking.time_from} - {booking.time_to}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">Amount Paid:</span>
                  <span className="text-green-600 font-bold">
                    {parseFloat(booking.student_paid_amount).toFixed(3)} BD
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 rounded-xl p-6 mb-6 text-left">
            <p className="text-gray-700 mb-3">
              <strong className="text-blue-700">✉️ Confirmation Email Sent</strong>
            </p>
            <p className="text-sm text-gray-600">
              We've sent a confirmation email with your meeting link and session details.
              Please check your inbox and add the session to your calendar.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGoToDashboard}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition shadow-lg flex items-center justify-center"
            >
              <FaHome className="mr-2" />
              Go to Dashboard
            </button>
            <button
              onClick={handleViewBooking}
              className="px-8 py-4 bg-white text-orange-600 border-2 border-orange-500 rounded-xl font-bold text-lg hover:bg-orange-50 transition flex items-center justify-center"
            >
              <FaCalendar className="mr-2" />
              View My Bookings
            </button>
          </div>

          <div className="mt-8 text-sm text-gray-500">
            <p>Need help? Contact us at support@taealam.bh</p>
          </div>
        </div>
      </div>
    );
  }

  // Payment Failed
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
      <div className="text-center bg-white rounded-2xl shadow-2xl p-12 max-w-2xl w-full">
        <div className="mb-6">
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <FaTimesCircle className="text-5xl text-red-500" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Payment Failed</h1>
          <p className="text-xl text-gray-600">We couldn't process your payment</p>
        </div>

        <div className="bg-red-50 rounded-xl p-6 mb-6 text-left border-2 border-red-200">
          <h3 className="text-lg font-bold text-red-700 mb-2">What happened?</h3>
          <p className="text-gray-700 mb-3">
            {error || `Payment status: ${paymentStatus}`}
          </p>
          <p className="text-sm text-gray-600">
            Your payment was not successful. This could be due to insufficient funds,
            card restrictions, or a network issue.
          </p>
        </div>

        <div className="bg-blue-50 rounded-xl p-6 mb-6 text-left">
          <h3 className="text-lg font-bold text-blue-700 mb-2">What should I do?</h3>
          <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
            <li>Check your card details and available balance</li>
            <li>Try a different payment method</li>
            <li>Contact your bank if you continue to experience issues</li>
            <li>Retry payment from your booking request (you still have time!)</li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleGoToDashboard}
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition shadow-lg flex items-center justify-center"
          >
            <FaHome className="mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="mt-8 text-sm text-gray-500">
          <p>Need assistance? Contact support@taealam.bh</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
